// URL builders: oocUrls.customer, oocUrls.customerDeliveryAddresses, oocUrls.customerPaymentMethods
// Implementation: import { oocApi } from '@/lib/apiClient', { oocUrls } from '@/lib/urlBuilder'
// Note: session-scoped endpoints — identity from JWT, no :id segment

import axios from 'axios'
import { oocApi } from '@/lib/apiClient'
import { oocUrls } from '@/lib/urlBuilder'
import { logError, logWarning } from '@/services/errorService'

export interface CustomerProfile {
	guid: string
	email?: string | null
	phone: string | null
	firstName?: string | null
	lastName?: string | null
}

// All fields optional — PATCH semantics, only send what changed.
// Channel restrictions (silently enforced by BE, but UI should respect):
//   email: send only for SMS-login customers (phone is identity anchor)
//   phone: send only for EMAIL-login customers (email is identity anchor)
//   firstName/lastName: always sendable
export interface UpdateCustomerRequest {
	firstName?: string
	lastName?: string
	email?: string
	phone?: string
}

export interface DeliveryAddress {
	guid: string
	addressLine1: string
	addressLine2?: string | null
	city: string
	state?: string | null
	zip: string
}

export interface DeliveryAddressesResponse {
	addresses: DeliveryAddress[]
}

export interface AddDeliveryAddressRequest {
	addressLine1: string
	addressLine2?: string | null
	city: string
	state?: string | null
	zip: string
}

interface AddDeliveryAddressResponse {
	address: DeliveryAddress
}

export interface PaymentMethod {
	guid: string
	locationId?: number
	locationName?: string | null
	maskedCardNumber: string
	expiration: string
	i4goUniqueId?: string | null
	// i4Go checkout fields — populated when the method originates from the checkout flow
	id?: string
	last4?: string
	brand?: string
	expiry?: string
}

export interface PaymentMethodsResponse {
	paymentMethods: PaymentMethod[]
}

export function getCustomer(): Promise<CustomerProfile> {
	return oocApi.get<CustomerProfile>(oocUrls.customer())
}

export function updateCustomer(body: UpdateCustomerRequest): Promise<CustomerProfile> {
	return oocApi.patch<UpdateCustomerRequest, CustomerProfile>(oocUrls.customer(), body)
}

export function logout(): Promise<void> {
	return oocApi.del(oocUrls.logout())
}

export function getDeliveryAddresses(): Promise<DeliveryAddress[]> {
	return oocApi
		.get<DeliveryAddressesResponse>(oocUrls.customerDeliveryAddresses())
		.then((response) => {
			if (!Array.isArray(response?.addresses)) {
				logWarning('[customer] getDeliveryAddresses: invalid response structure', {
					status: 'invalid_structure',
				})
				return []
			}
			return response.addresses
		})
		.catch((error) => {
			const errorMessage = error instanceof Error ? error.message : 'Unknown error'
			logError('[customer] getDeliveryAddresses failed', {
				message: errorMessage,
				status: 'fetch_failed',
			})
			throw error
		})
}

export async function addDeliveryAddress(body: AddDeliveryAddressRequest): Promise<void> {
	try {
		await oocApi.post<AddDeliveryAddressRequest, AddDeliveryAddressResponse>(
			oocUrls.customerDeliveryAddresses(),
			body,
		)
	} catch (error) {
		// 409 means this address is already saved — treat as success per product decision
		if (axios.isAxiosError(error) && error.response?.status === 409) return
		throw error
	}
}

export function deleteDeliveryAddress(guid: string): Promise<void> {
	return oocApi.del(`${oocUrls.customerDeliveryAddresses()}/${encodeURIComponent(guid)}`)
}

export function getPaymentMethods(locationId?: number): Promise<PaymentMethod[]> {
	return oocApi
		.get<PaymentMethodsResponse>(oocUrls.customerPaymentMethods())
		.then((response) => {
			if (!Array.isArray(response?.paymentMethods)) {
				logWarning('[customer] getPaymentMethods: invalid response structure', {
					status: 'invalid_structure',
				})
				return []
			}
			// V1 parity: filter by locationId so only cards saved at this location are shown.
			if (locationId !== undefined) {
				return response.paymentMethods.filter((m) => m.locationId === locationId)
			}
			return response.paymentMethods
		})
		.catch((error) => {
			const errorMessage = error instanceof Error ? error.message : 'Unknown error'
			logError('[customer] getPaymentMethods failed', {
				message: errorMessage,
				status: 'fetch_failed',
			})
			throw error
		})
}

export interface AddPaymentMethodRequest {
	locationId: number
	i4goUniqueId: string
	maskedCardNumber: string
	expiration: string
}

export function addPaymentMethod(body: AddPaymentMethodRequest): Promise<PaymentMethod> {
	return oocApi.post<AddPaymentMethodRequest, PaymentMethod>(
		oocUrls.customerPaymentMethods(),
		body,
	)
}

export function deletePaymentMethod(guid: string): Promise<void> {
	return oocApi.del(`${oocUrls.customerPaymentMethods()}/${encodeURIComponent(guid)}`)
}
